import React, { useCallback, useEffect, useMemo, useState } from "react";
import DataTable, { TableColumn } from "react-data-table-component";
import { Modal } from "react-bootstrap";
import { toast } from "react-toastify";
import { apiAxios } from "../../../../utils/apiAxios";

import {
  DeletedFileHistoryRow,
} from "../Interfaces/AdminFileManagementRow";

type DeletedHistoryBatchRow = {
  groupKey: string;
  groupID: number;
  deletionType: string;
  fromDate: string | null;
  toDate: string | null;
  deletedAt: string;
  deletedByName: string;
  totalFiles: number;
  files: DeletedFileHistoryRow[];
};

const AdminDeletedFileHistory: React.FC = () => {
  const [historyRows, setHistoryRows] = useState<DeletedFileHistoryRow[]>([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historySearch, setHistorySearch] = useState("");
  const [expandedGroupKey, setExpandedGroupKey] = useState<string | null>(null);

  const fetchDeletedHistory = useCallback(async () => {
    try {
      setHistoryLoading(true);

      const response = await apiAxios.get(
        "/Central/file-management/deleted-history"
      );

      const json = response.data;

      if (!json?.isSuccess) {
        toast.error(json?.status || "Failed to load deleted history");
        setHistoryRows([]);
        return;
      }

      const result = json.dataOne ?? json.data ?? [];
      const items = Array.isArray(result) ? result : result?.data ?? [];

      const mappedRows: DeletedFileHistoryRow[] = items.map(
        (item: any) => ({
          historyID: Number(item.historyID ?? item.HistoryID ?? 0),
          completionDate:
            item.completionDate ?? item.CompletionDate ?? null,
          groupID: Number(item.groupID ?? item.GroupID ?? 0),
          caseID: item.caseID ?? item.CaseID ?? null,
          fileName: String(item.fileName ?? item.FileName ?? ""),
          filePath: String(item.filePath ?? item.FilePath ?? ""),
          fileType: String(item.fileType ?? item.FileType ?? ""),
          deletionType: String(
            item.deletionType ?? item.DeletionType ?? ""
          ),
          deletedBy: item.deletedBy ?? item.DeletedBy ?? null,
          deletedAt: String(item.deletedAt ?? item.DeletedAt ?? ""),
          deletionStatus: String(
            item.deletionStatus ?? item.DeletionStatus ?? ""
          ),
          fromDate: item.fromDate ?? item.FromDate ?? null,
          toDate: item.toDate ?? item.ToDate ?? null,
          deletedByName: String(
            item.deletedByName ?? item.DeletedByName ?? "Unknown User"
          ),
        })
      );

      setHistoryRows(mappedRows);
    } catch (error: any) {
      console.error("Error fetching deleted history:", error);

      toast.error(
        error?.response?.data?.status ||
          error?.response?.data?.message ||
          "Failed to load deleted history"
      );

      setHistoryRows([]);
    } finally {
      setHistoryLoading(false);
    }
  }, []);

  useEffect(() => {
    void fetchDeletedHistory();
  }, [fetchDeletedHistory]);

  const formatDisplayDate = (
    value: string | Date | null | undefined
  ): string => {
    if (!value) return "-";

    if (typeof value === "string") {
      const match = value.match(/^(\d{4})-(\d{2})-(\d{2})$/);

      if (match) {
        const [, year, month, day] = match;

        return new Date(
          Number(year),
          Number(month) - 1,
          Number(day)
        ).toLocaleDateString("en-GB", {
          day: "2-digit",
          month: "short",
          year: "numeric",
        });
      }
    }

    const date = value instanceof Date ? value : new Date(value);

    if (Number.isNaN(date.getTime())) return "-";

    return date.toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  const formatDisplayDateTime = (
    value: string | Date | null | undefined
  ): string => {
    if (!value) return "-";

    const date = value instanceof Date ? value : new Date(value);

    if (Number.isNaN(date.getTime())) return "-";

    const datePart = date.toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });

    const timePart = date
      .toLocaleTimeString("en-GB", {
        hour: "2-digit",
        minute: "2-digit",
        hour12: true,
      })
      .toLowerCase();

    return `${datePart}, ${timePart}`;
  };

  const historyBatchRows = useMemo(() => {
    const groups = historyRows.reduce<
      Record<string, DeletedHistoryBatchRow>
    >((result, file) => {
      const groupKey =
        file.groupID > 0
          ? String(file.groupID)
          : `individual-${file.historyID}`;

      if (!result[groupKey]) {
        result[groupKey] = {
          groupKey,
          groupID: file.groupID,
          deletionType: file.deletionType,
          fromDate: file.fromDate,
          toDate: file.toDate,
          deletedAt: file.deletedAt,
          deletedByName: file.deletedByName,
          totalFiles: 0,
          files: [],
        };
      }

      result[groupKey].files.push(file);
      result[groupKey].totalFiles++;

      return result;
    }, {});

    return Object.values(groups).sort(
      (a, b) =>
        new Date(b.deletedAt).getTime() -
        new Date(a.deletedAt).getTime()
    );
  }, [historyRows]);

  const filteredHistoryRows = useMemo(() => {
    const search = historySearch.trim().toLowerCase();

    if (!search) return historyBatchRows;

    return historyBatchRows.filter(
      (row) =>
        String(row.groupID).includes(search) ||
        row.deletionType.toLowerCase().includes(search) ||
        row.deletedByName.toLowerCase().includes(search) ||
        String(row.fromDate ?? "").toLowerCase().includes(search) ||
        String(row.toDate ?? "").toLowerCase().includes(search) ||
        row.files.some(
          (file) =>
            String(file.caseID ?? "").includes(search) ||
            file.fileName.toLowerCase().includes(search) ||
            file.fileType.toLowerCase().includes(search) ||
            file.deletionStatus.toLowerCase().includes(search)
        )
    );
  }, [historyBatchRows, historySearch]);

  const historyColumns: TableColumn<DeletedHistoryBatchRow>[] = [
    {
      name: "Group ID",
      selector: (row) => row.groupID || "-",
      sortable: true,
      width: "100px",
    },
    {
      name: "Deletion Type",
      selector: (row) => row.deletionType || "-",
      sortable: true,
      width: "130px",
    },
    {
      name: "From Date",
      selector: (row) => row.fromDate ?? "",
      cell: (row) => formatDisplayDate(row.fromDate),
      sortable: true,
      width: "130px",
    },
    {
      name: "To Date",
      selector: (row) => row.toDate ?? "",
      cell: (row) => formatDisplayDate(row.toDate),
      sortable: true,
      width: "130px",
    },
    {
      name: "Deleted At",
      selector: (row) => row.deletedAt,
      cell: (row) => formatDisplayDateTime(row.deletedAt),
      sortable: true,
      minWidth: "180px",
    },
    {
      name: "Deleted By",
      selector: (row) => row.deletedByName,
      sortable: true,
      minWidth: "150px",
    },
    {
      name: "Total Files",
      selector: (row) => row.totalFiles,
      sortable: true,
      center: true,
      width: "110px",
    },
    {
      name: "Action",
      cell: (row) => (
        <button
          type="button"
          className="btn btn-outline-primary btn-sm"
          onClick={() => setExpandedGroupKey(row.groupKey)}
        >
          <i className="bi bi-eye me-1" />
          View Files
        </button>
      ),
      width: "125px",
      center: true,
    },
  ];

  const historyFileColumns: TableColumn<DeletedFileHistoryRow>[] = [
    {
      name: "Case ID",
      selector: (row) => row.caseID ?? "-",
      sortable: true,
      width: "100px",
    },
    {
      name: "File Name",
      selector: (row) => row.fileName,
      sortable: true,
      wrap: true,
      minWidth: "220px",
    },
    {
      name: "File Type",
      selector: (row) => row.fileType || "-",
      width: "110px",
    },
    {
      name: "Deleted By",
      selector: (row) => row.deletedByName || "-",
      minWidth: "150px",
    },
    {
      name: "Completion Date",
      selector: (row) => row.completionDate ?? "",
      cell: (row) => formatDisplayDate(row.completionDate),
      sortable: true,
      width: "150px",
    },
    {
      name: "Status",
      selector: (row) => row.deletionStatus,
      cell: (row) => {
        const status = row.deletionStatus.toLowerCase();

        return (
          <span
            className={`badge ${
              status === "success"
                ? "bg-success"
                : status === "failed"
                ? "bg-danger"
                : "bg-warning text-dark"
            }`}
          >
            {row.deletionStatus || "-"}
          </span>
        );
      },
      width: "110px",
    },
  ];

  const selectedGroup = historyBatchRows.find(
    (row) => row.groupKey === expandedGroupKey
  );

  return (
    <section className="afm-table-card">
      <div className="afm-table-heading">
        <div>
          <h2 className="afm-section-title">Deleted File History</h2>
          <p className="afm-section-note">
            Review files deleted individually or in bulk.
          </p>
        </div>

        <button
          type="button"
          className="btn btn-outline-primary btn-sm"
          onClick={() => void fetchDeletedHistory()}
          disabled={historyLoading}
        >
          <i className="bi bi-arrow-clockwise me-1" />
          Refresh
        </button>
      </div>

      <div className="mb-3" style={{ maxWidth: "350px" }}>
        <input
          type="text"
          className="form-control"
          placeholder="Search Group ID, Case ID or filename..."
          value={historySearch}
          onChange={(event) => setHistorySearch(event.target.value)}
        />
      </div>

      <DataTable
        columns={historyColumns}
        data={filteredHistoryRows}
        keyField="groupKey"
        pagination
        paginationPerPage={10}
        paginationRowsPerPageOptions={[10, 20, 50, 100]}
        highlightOnHover
        striped
        responsive
        progressPending={historyLoading}
        fixedHeader
        fixedHeaderScrollHeight="500px"
        persistTableHead
        noDataComponent={
          <div className="p-4 text-muted">
            No deleted file history found.
          </div>
        }
        className="admin-delivery-table"
      />

      <Modal
        show={expandedGroupKey !== null}
        onHide={() => setExpandedGroupKey(null)}
        size="xl"
        centered
        scrollable
        dialogClassName="custom-top-modal"
      >
        <Modal.Header closeButton>
          <Modal.Title>
            <i className="bi bi-folder2-open me-2 text-primary" />
            Deleted Files
            {selectedGroup && (
              <small className="text-muted ms-2">
                {selectedGroup.totalFiles} files
              </small>
            )}
          </Modal.Title>
        </Modal.Header>

        <Modal.Body>
          {!selectedGroup ? (
            <p className="text-muted mb-0">No files found.</p>
          ) : (
            <DataTable
              columns={historyFileColumns}
              data={selectedGroup.files}
              keyField="historyID"
              pagination
              paginationPerPage={5}
              paginationRowsPerPageOptions={[5, 10, 20]}
              highlightOnHover
              striped
              responsive
              noDataComponent="No files found."
              className="admin-delivery-table"
            />
          )}
        </Modal.Body>

        <Modal.Footer>
          <button
            type="button"
            className="btn btn-secondary"
            onClick={() => setExpandedGroupKey(null)}
          >
            Close
          </button>
        </Modal.Footer>
      </Modal>
    </section>
  );
};

export default AdminDeletedFileHistory;